import moment from "moment";

export function escapeRegExp(str = "") {
  return String(str).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function escapeDollarForReplace(val = "") {
  return String(val).split("$").join("$$");
}

function toUrlSafe(val = "") {
  return encodeURIComponent(String(val || "").trim());
}

function pickGuideBase(settings = {}) {
  return settings?.guide_base_url || "https://guide.ruebarue.com";
}

export function subButtons(body = "", customButtons = [], subMacrosFn) {
  let out = String(body);

  (customButtons || []).forEach((button) => {
    if (!button?.macro) return;
    const pattern = new RegExp(escapeRegExp(button.macro), "g");

    if (pattern.test(out)) {
      const title = subMacrosFn ? subMacrosFn(button.label || "") : (button.label || "");
      const url = subMacrosFn ? subMacrosFn(button.url || "") : (button.url || "");
      out = out.replace(pattern, `[${title}](${url} :request)`);
    }
  });

  return out;
}

export function subCustomMacros(body = "", macros = [], values = []) {
  let out = String(body || "");

  (macros || []).forEach((macro) => {
    const token = String(macro?.macro || "").trim(); // e.g. "[Directions]"
    if (!token) return;

    // matches the literal token exactly (same as old Utils.escapeRegExp(macro.macro))
    const re = new RegExp(escapeRegExp(token), "g");

    if (re.test(out)) {
      // old logic: values filtered by macro_id === macro.id
      const val =
        ((values || []).find((v) => +v?.macro_id === +macro?.id) || {})
          ?.content || "";

      // important: allow $ in replacement
      out = out.replace(re, escapeDollarForReplace(val));
    }
  });

  return out;
}

function getCustomMacros(settings = {}) {
  return (
    settings?.custom_macros ||
    settings?.guestbook?.custom_macros ||
    settings?.settings?.custom_macros ||          // in case you passed SettingsContext wrapper
    settings?.settings?.guestbook?.custom_macros ||
    []
  );
}

function getCustomButtons(settings = {}) {
  return (
    settings?.custom_buttons ||
    settings?.guestbook?.custom_buttons ||
    settings?.settings?.custom_buttons ||
    settings?.settings?.guestbook?.custom_buttons ||
    []
  );
}

function getCustomValues(reservation = {}, rental = {}) {
  return (
    rental?.custom_macro_values ||
    reservation?.rental?.custom_macro_values ||
    reservation?.custom_macro_values ||
    []
  );
}

export function subStandardMacros(
  body = "",
  reservation = {},
  rental = {},
  branding = {},
  guestlink,
  settings = {}
  ) {

  let out = String(body);

  const guideBase = pickGuideBase(settings);

  const resMacros = {
    door_code: (b) =>
      b.replace(
        /\[DoorCode\]/g,
        !reservation.manual_door_code
          ? reservation.door_code || ""
          : reservation.manual_door_code
      ),

    reservation_id: "[ReservationId]",
    native_id: "[NativeId]",

    reservation_code: (b) =>
      b.replace(/\[GuestSMSCode\]/g, reservation.reservation_code ? `#${reservation.reservation_code}` : ""),

    wifi_name: "[WifiName]",
    wifi_pass: "[WifiPassword]",
    first_name: "[FirstName]",
    last_name: "[LastName]",
    email: "[Email]",
    phone: "[PhoneNumber]",

    check_in: (b) =>
      b.replace(
        /\[CheckIn\]/g,
        reservation.check_in ? moment(reservation.check_in, "YYYY-MM-DD").format("MMM D, YYYY") : ""
      ),
    check_out: (b) =>
      b.replace(
        /\[CheckOut\]/g,
        reservation.check_out ? moment(reservation.check_out, "YYYY-MM-DD").format("MMM D, YYYY") : ""
      ),

    check_in_time: "[CheckInTime]",
    check_out_time: "[CheckOutTime]",

    check_in_link: (b) =>
      b.replace(
        /\[GuestCheckIn\]/g,
        reservation.guest_reservation_id
          ? `[Check In](${guideBase}/check-in/${reservation.guest_reservation_id} :request)`
          : ""
      ),

    check_out_link: (b) =>
      b.replace(
        /\[GuestCheckOut\]/g,
        reservation.guest_reservation_id
          ? `[Check Out](${guideBase}/check-out/${reservation.guest_reservation_id} :request)`
          : ""
      ),

    early_check_in: (b) =>
      b.replace(
        /\[EarlyCheckIn\]/g,
        guestlink ? `[Request Early Check In](${guideBase}/early-check-in/${guestlink} :request)` : ""
      ),

    late_check_out: (b) =>
      b.replace(
        /\[LateCheckOut\]/g,
        guestlink ? `[Request Late Check Out](${guideBase}/late-check-out/${guestlink} :request)` : ""
      ),

    survey: (b) =>
      b.replace(/\[Survey-(\d+)\]/g, (match, surveyId) => {
        return guestlink ? `${guideBase}/survey/${surveyId}/${guestlink}` : "";
      }),

    five_star_review: (b) =>
      b.replace(
        /\[FiveStarReview\]/g,
        reservation?.guestlink_id ? `${guideBase}/review/${reservation.guestlink_id}` : ""
      ),

    five_star_link: (b) =>
      b.replace(
        /\[FiveStarLink\]/g,
        reservation?.guestlink_id ? `${guideBase}/review/${reservation.guestlink_id}` : ""
      ),

    source: "[Source]",
  };

  const propertyMacros = {
    pms_id: "[PropertyId]",

    property_code: (b) =>
      b.replace(/\[PropertySMSCode\]/g, rental.property_code ? `#${rental.property_code}` : ""),

    address: (b) =>
      !reservation.rental_address
        ? b.replace(/\[PropertyAddress\]/g, "")
        : b.replace(
            /\[PropertyAddress\]/g,
            `${reservation.rental_address}`
          ),

    destination_guide_link: (b) => {
      const destId = reservation?.destination_id ?? "";
      const url = destId
        ? `${guideBase}/destination/${toUrlSafe(destId)}`
        : "";

      return b.replace(/\[DestinationGuideLink\]/g, url);
    },

    property_guide_link: (b) => {
      const url = `${guideBase}/guestbook/${guestlink || 0}`;
      return b.replace(/\[PropertyGuideLink\]/g, url);
    },
  };

  const settingsMacros = {
    company_name: "[CompanyName]",
  };

  const replaceMacros = (b, macros, data) => {
    let next = String(b);
    for (const key in macros) {
      const entry = macros[key];
      if (typeof entry === "function") {
        next = entry(next, data);
      } else {
        const pattern = new RegExp(escapeRegExp(entry), "g");
        const val = escapeDollarForReplace((data?.[key] || ""));
        next = next.replace(pattern, val);
      }
    }
    return next;
  };

  out = replaceMacros(out, resMacros, reservation);
  out = replaceMacros(out, propertyMacros, rental);
  out = replaceMacros(out, settingsMacros, branding);

  return out;
}

export function renderScheduledMessageBody({
  template = "",
  reservation = {},
  rental = {},
  settings = {},
  guestlink = 0,
} = {}) {

  const customButtons = getCustomButtons(settings);
  const customMacros = getCustomMacros(settings);
  const customValues = getCustomValues(reservation, rental);

  const branding =
    settings?.branding ||
    settings?.settings?.branding ||
    {};

  const subAll = (txt) => {
    let out = String(txt || "");
    out = subCustomMacros(out, customMacros, customValues);
    out = subStandardMacros(out, reservation, rental, branding, guestlink, settings);
    return out;
  };

  let out = String(template || "");
  out = subButtons(out, customButtons, subAll);
  out = subAll(out);

  return out;
}